/**
 * Coverage context — the selection universe (entities, cycles, boundaries)
 * and its validation. One hook shared by the /coverage resolver and the
 * canonical /cycles/:cycleId/coverage page so both routes validate context
 * identically (docs/COVERAGE_ROUTE_CONTRACT.md).
 *
 * Fetches SELECTION OPTIONS only — never coverage results. Coverage data is
 * fetched by CoverageView, and only after this context validates.
 */
import { useEffect, useState } from "react";
import { apiFetch } from "../../components/esg/EsgKit";

/** Explicit no-consolidation scope. It is a real, declared denominator (the
 *  single selected entity) — offered as a choice the user must actively make,
 *  never applied silently. */
export const ENTITY_ONLY_BOUNDARY = Object.freeze({
  boundary_id: "entity-only",
  boundary_type: "entity_only",
  label: "Entity-only (no consolidation)",
  version: 0,
});

export const CONTEXT_KEYS = ["entity", "boundary", "framework", "requirement", "metric", "lens"];

/** Canonical URL builder — preserves framework/requirement/metric/lens. */
export function buildCoveragePath(cycleId, params) {
  const qs = new URLSearchParams();
  for (const k of CONTEXT_KEYS) {
    const v = params.get(k);
    if (v) qs.set(k, v);
  }
  const q = qs.toString();
  return `/cycles/${encodeURIComponent(cycleId)}/coverage${q ? `?${q}` : ""}`;
}

export default function useCoverageContext() {
  const [entities, setEntities] = useState(null);
  const [cycles, setCycles] = useState(null);
  const [boundaries, setBoundaries] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const fail = (e) => { if (alive) setError({ message: e.message, status: e.status }); };
    apiFetch("/api/v1/enterprise/entities")
      .then((r) => alive && setEntities(r.entities || [])).catch(fail);
    apiFetch("/api/v1/spine/cycles")
      .then((r) => alive && setCycles(r.cycles || [])).catch(fail);
    apiFetch("/api/spine/v1/ent/boundaries")
      .then((r) => alive && setBoundaries(r.boundaries || [])).catch(fail);
    return () => { alive = false; };
  }, []);

  const loading = !error && (entities === null || cycles === null || boundaries === null);

  const findCycle = (id) => (cycles || []).find((c) => c.cycle_id === id) || null;
  const findEntity = (id) => (entities || []).find((e) => e.entity_id === id) || null;
  const resolveBoundary = (id) => {
    if (id === ENTITY_ONLY_BOUNDARY.boundary_id) return ENTITY_ONLY_BOUNDARY;
    return (boundaries || []).find((b) => b.boundary_id === id) || null;
  };

  /**
   * Validate a (cycleId, entityId, boundaryId) triple against the loaded
   * universe. Returns { ok } or a typed failure — never a fallback:
   *   incomplete_context | invalid_cycle | invalid_entity | invalid_boundary
   */
  const validate = (cycleId, entityId, boundaryId) => {
    if (!cycleId || !entityId || !boundaryId)
      return { ok: false, type: "incomplete_context", missing: [
        !cycleId && "cycle", !entityId && "entity", !boundaryId && "boundary",
      ].filter(Boolean) };
    if (!findCycle(cycleId)) return { ok: false, type: "invalid_cycle", id: cycleId };
    if (!findEntity(entityId)) return { ok: false, type: "invalid_entity", id: entityId };
    if (!resolveBoundary(boundaryId)) return { ok: false, type: "invalid_boundary", id: boundaryId };
    return { ok: true };
  };

  return { entities, cycles, boundaries, loading, error,
           findCycle, findEntity, resolveBoundary, validate };
}
